// =============================================================================
// features/relatorios/hooks/useReportFilters.ts — Estado dos filtros de
// relatórios serializado na URL (F23-S06).
//
// Estado serializado em searchParams para deep-link e reload-safe:
//   ?range=last30d&scope=global&cityIds=uuid1,uuid2&personId=uuid&compareWithPrevious=true
//   ?range=custom&dateFrom=2026-08-01&dateTo=2026-08-21
//
// `range=custom` sempre foi aceito pela API, mas a UI não oferecia — quem quisesse
// "só o dia 12" não tinha como pedir (diagnóstico D5).
//
// Defaults:
//   range: 'last30d'
//   scope: inferido do papel (self → agente; city → 1 cidade; global → admin/gestor_geral)
//   cityIds: []
//   compareWithPrevious: false
// =============================================================================

import type { ReportRange, ReportScope } from '@elemento/shared-schemas';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface ReportFilters {
  range: ReportRange;
  /** Início do período customizado (YYYY-MM-DD). '' quando range ≠ custom. */
  dateFrom: string;
  /** Fim do período customizado (YYYY-MM-DD). '' quando range ≠ custom. */
  dateTo: string;
  scope: ReportScope;
  cityIds: string[];
  /** users.id da pessoa no filtro "por pessoa". null = sem recorte. */
  personId: string | null;
  compareWithPrevious: boolean;
}

export interface ReportFiltersActions {
  setRange: (range: ReportRange) => void;
  setCustomDates: (from: string, to: string) => void;
  setScope: (scope: ReportScope) => void;
  setCityIds: (ids: string[]) => void;
  setPersonId: (id: string | null) => void;
  setCompareWithPrevious: (v: boolean) => void;
}

// ---------------------------------------------------------------------------
// Valores válidos para validação de input vindo da URL
// ---------------------------------------------------------------------------

const VALID_RANGES = new Set<ReportRange>([
  'today',
  'yesterday',
  'thisWeek',
  'last7d',
  'last30d',
  'last90d',
  'thisMonth',
  'lastMonth',
  'ytd',
  'custom',
]);

/** Data de calendário simples — o formato que o input[type=date] produz. */
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const VALID_SCOPES = new Set<ReportScope>(['global', 'city', 'self']);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Gerencia os filtros do módulo de relatórios na URL.
 * O defaultScope deve ser calculado pelo caller com base no papel do usuário.
 */
export function useReportFilters(
  defaultScope: ReportScope = 'self',
): ReportFilters & ReportFiltersActions {
  const [params, setParams] = useSearchParams();

  // ── Leitura (com validação defensiva dos valores da URL) ─────────────────

  const range = ((): ReportRange => {
    const v = params.get('range');
    if (v && VALID_RANGES.has(v as ReportRange)) return v as ReportRange;
    return 'last30d';
  })();

  const scope = ((): ReportScope => {
    const v = params.get('scope');
    if (v && VALID_SCOPES.has(v as ReportScope)) return v as ReportScope;
    return defaultScope;
  })();

  const cityIds = ((): string[] => {
    // Aceita formato: cityIds=uuid1&cityIds=uuid2 (repeat) ou cityIds=uuid1,uuid2 (comma)
    const repeated = params.getAll('cityIds');
    if (repeated.length > 0) return repeated.flatMap((v) => v.split(',').filter(Boolean));
    const single = params.get('cityIds');
    if (single) return single.split(',').filter(Boolean);
    return [];
  })();

  // Validação defensiva: qualquer coisa fora de YYYY-MM-DD vinda da URL é
  // descartada em vez de virar um 400 no backend.
  const readDay = (key: string): string => {
    const v = params.get(key);
    return v !== null && DAY_PATTERN.test(v) ? v : '';
  };
  const dateFrom = readDay('dateFrom');
  const dateTo = readDay('dateTo');

  const personId = params.get('personId');

  const compareWithPrevious = params.get('compareWithPrevious') === 'true';

  // ── Setters (atualizam a URL, preservam outros params) ───────────────────

  const setRange = React.useCallback(
    (next: ReportRange) => {
      setParams((prev) => {
        const next_params = new URLSearchParams(prev);
        next_params.set('range', next);
        // Sair de 'custom' descarta as datas: deixá-las na URL faria um
        // deep-link de "Hoje" carregar com um intervalo fantasma no endereço.
        if (next !== 'custom') {
          next_params.delete('dateFrom');
          next_params.delete('dateTo');
        }
        return next_params;
      });
    },
    [setParams],
  );

  const setCustomDates = React.useCallback(
    (from: string, to: string) => {
      setParams((prev) => {
        const next_params = new URLSearchParams(prev);
        if (from === '') next_params.delete('dateFrom');
        else next_params.set('dateFrom', from);
        if (to === '') next_params.delete('dateTo');
        else next_params.set('dateTo', to);
        return next_params;
      });
    },
    [setParams],
  );

  const setScope = React.useCallback(
    (next: ReportScope) => {
      setParams((prev) => {
        const next_params = new URLSearchParams(prev);
        next_params.set('scope', next);
        // Ao trocar scope, limpar cityIds/personId (podem não fazer sentido no novo scope)
        next_params.delete('cityIds');
        next_params.delete('personId');
        return next_params;
      });
    },
    [setParams],
  );

  const setCityIds = React.useCallback(
    (ids: string[]) => {
      setParams((prev) => {
        const next_params = new URLSearchParams(prev);
        next_params.delete('cityIds');
        for (const id of ids) next_params.append('cityIds', id);
        return next_params;
      });
    },
    [setParams],
  );

  const setPersonId = React.useCallback(
    (id: string | null) => {
      setParams((prev) => {
        const next_params = new URLSearchParams(prev);
        if (id === null) next_params.delete('personId');
        else next_params.set('personId', id);
        return next_params;
      });
    },
    [setParams],
  );

  const setCompareWithPrevious = React.useCallback(
    (v: boolean) => {
      setParams((prev) => {
        const next_params = new URLSearchParams(prev);
        if (v) {
          next_params.set('compareWithPrevious', 'true');
        } else {
          next_params.delete('compareWithPrevious');
        }
        return next_params;
      });
    },
    [setParams],
  );

  return {
    range,
    dateFrom,
    dateTo,
    scope,
    cityIds,
    personId,
    compareWithPrevious,
    setRange,
    setCustomDates,
    setScope,
    setCityIds,
    setPersonId,
    setCompareWithPrevious,
  };
}
